import React, { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Container } from "components";
import Breadcrumb from "components/molecules/Breadcrumb";
import { useDispatch, useSelector } from "react-redux";
import { getData } from "actions";
import { API_URL_rating } from "constants/api";
import { ratingReducers } from "redux/ratingSlice";
import useDebounce from "hooks/useDebounce";
import { BiSortDown, BiSortUp } from "react-icons/bi";
import { FaEdit } from "react-icons/fa";
import { SyncLoader } from "react-spinners";
import { AuthContext } from "context/AuthContext";

// field for use sort up/down
const TABLE_HEAD = [
  { title: "Name", field: "" },
  { title: "Rating Area", field: "" },
  { title: "Actions", field: "" },
];

const Ratings = () => {
  const navigate = useNavigate();
  const [adminAccess, setAdminAccess] = useState(false);
  const { datauser } = useContext(AuthContext);
  const dispatch = useDispatch();
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState("");
  const [sortColumn, setSortColumn] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [currentPage, setCurrentPage] = useState(0);

  const {
    getRatingResult,
    getRatingLoading,
    getRatingError,
    addRatingResult,
    deleteRatingResult,
  } = useSelector((state) => state.rating);

  const get = useCallback(
    async (params) => {
      getData(
        API_URL_rating,
        params,
        { dispatch, redux: ratingReducers },
        "GET_RATING"
      );
    },
    [dispatch]
  );

  const onEdit = (item) => {
    navigate(`/administrator/tickets/ratings/edit`, {
      state: {
        item,
      },
    });
  };

  const delayedSearch = useCallback(
    useDebounce((value) => {
      //RESET PAGINATE KE 1
      setOffset(0);
      setCurrentPage(0);
      const params = `?limit=${limit}&offset=${offset}&search=${value}&ordering=${
        sortOrder === "desc" ? "-" : ""
      }${sortColumn}`;
      get(params);
    }, 1000),
    [limit, offset, sortOrder, sortColumn]
  );

  const handleSort = (column) => {
    //RESET PAGINATE KE 1
    setOffset(0);
    setCurrentPage(0);
    if (column === sortColumn) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  // Sort icons
  const renderSortIcon = (field) => {
    if (field === sortColumn) {
      return sortOrder === "asc" ? (
        <BiSortUp className="ml-1 h-4" />
      ) : (
        <BiSortDown className="ml-1 h-4" />
      );
    }
    return <BiSortUp className="ml-1 h-4 text-gray-300" />;
  };

  const action = [
    {
      name: "Edit",
      icon: <FaEdit />,
      func: onEdit,
      colorlight: "bg-sky-500",
      colordark: "dark:bg-sky-800",
      hoverlight: "hover:bg-sky-600",
      hoverdark: "hover:dark:bg-sky-900",
    },
  ];

  const fetchData = useCallback(() => {
    const params = `?limit=${limit}&offset=${offset}&search=${search}&ordering=${
      sortOrder === "desc" ? "-" : ""
    }${sortColumn}`;
    get(params);
  }, [get, limit, offset, sortColumn, sortOrder]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (addRatingResult) {
      fetchData();
    }
  }, [addRatingResult, fetchData]);

  useEffect(() => {
    if (deleteRatingResult) {
      fetchData();
    }
  }, [deleteRatingResult, fetchData]);

  useEffect(() => {
    if (datauser !== "") {
      const permissionViewMenuAdmin = datauser.group_permissions.includes(
        "access_menu_admin"
      );
      if (permissionViewMenuAdmin) {
        setAdminAccess(true);
      } else {
        navigate("/dashboard/agent");
      }
    }
  }, [datauser]);

  if (!adminAccess) {
    return null;
  }

  return (
    <Container>
      <Breadcrumb
        title={"Settings"}
        excludedPaths={["tickets", "administrator"]}
        group={"Superadmin"}
      />
      <div className="bg-neutral dark:bg-neutral-800 rounded overflow-hidden shadow">
        <div className="h-12 w-full grid items-center px-4 bg-neutral-100 dark:bg-neutral-700 border-b border-neutral-200 dark:border-neutral-600 text-xs md:text-base">
          Current ratings
        </div>

        <div className="flex flex-col md:flex-row text-center md:justify-between align-middle px-4 my-4 bg-neutral dark:bg-neutral-800 "></div>

        {/* <CardContainer title="List of agents"> */}
        <div className="mx-4">
          <div className="overflow-x-auto min-h-[55vh]">
            <table className="border border-collapse items-center w-full">
              <thead className="text-left">
                <tr>
                  {TABLE_HEAD.map((item, itemIdx) => (
                    <th
                      key={itemIdx}
                      className="p-2 border text-sm whitespace-nowrap cursor-pointer"
                      onClick={() => {
                        item.field && handleSort(item.field);
                      }}
                    >
                      <span className="flex">
                        {item.title}
                        {item.field && renderSortIcon(item.field)}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* loading  */}
                {getRatingLoading && (
                  <tr>
                    <td
                      className="text-center py-12"
                      colSpan={TABLE_HEAD.length + 1}
                    >
                      <div className="pt-10 pb-6 flex justify-center items-center">
                        <SyncLoader color="#4477CE" />
                      </div>
                    </td>
                  </tr>
                )}
                {/* Error */}
                {getRatingError && (
                  <tr>
                    <td className="text-center" colSpan={TABLE_HEAD.length + 1}>
                      <div className="pt-20 pb-12 flex justify-center items-center text-xs text-red-500">
                        {getRatingError}
                      </div>
                    </td>
                  </tr>
                )}
                {/* Result = 0 */}
                {getRatingResult.results &&
                  getRatingResult.results.length === 0 && (
                    <tr>
                      <td
                        className="text-center"
                        colSpan={TABLE_HEAD.length + 1}
                      >
                        <div className="pt-20 pb-12 flex justify-center items-center text-xs text-slate-600 dark:text-white">
                          No Data
                        </div>
                      </td>
                    </tr>
                  )}
                {getRatingResult.results &&
                  getRatingResult.results.map((item, index) => (
                    <tr key={index}>
                      <td className="p-4 border border-blue-gray-50 w-1/4">
                        {item.name}
                      </td>

                      <td className="p-4 border border-blue-gray-50 w-1/4">
                        {item.rating_area}
                      </td>
                      <td className="p-4 border border-blue-gray-50">
                        <div className="flex">
                          {action.map((action, actionIdx) => (
                            <button
                              key={actionIdx}
                              className={`mx-0.5 text-white text-sm ${action.colorlight} ${action.colordark} ${action.hoverlight} ${action.hoverdark} rounded p-1 `}
                              onClick={() => action.func(item)}
                            >
                              <div className="flex items-center">
                                {action.icon} {action.name}
                              </div>
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <div className="pt-5 py-11"></div>
        </div>
        {/* </CardContainer> */}
      </div>
    </Container>
  );
};

export default Ratings;
